import {
    faArrowRightFromBracket,
    faBars,
    faBell,
    faCircleQuestion,
    faExpand,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import screenfull from "screenfull";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { getRootPath } from "userful-chronos-app-common-js/dist/routing";
import { useAuthenticationContext } from "userful-chronos-common-store/dist/auth";
import Button from "../button/Button";
import PerpetualModeBanner from "../Container/perpetualmodecheck/PerpetualModeBanner";
import DropdownItem from "../Widgets/Dropdown/DropdownItem";
import UserfulTooltip from "../Widgets/UserfulTooltip";
import CapacityNotification from "./Capacity/CapacityNotification";
import "./NavBar.scss";
import NavBarTitle from "./NavBarTitle";

const COOKIE_KEY_PREFIX = "userful-notification-";
const getLocationSearch = () => (typeof window === "undefined" ? "" : window.location.search);

interface IProps {
    navigation?: Function;
    onLogout?: Function;
    showSearch?: boolean;
    showHome?: boolean;
    showLightBulb?: boolean;
    showHelp?: boolean;
    showBolt?: boolean;
    showWindowMax?: boolean;
    showFlag?: boolean;
    showBell?: boolean;
    hideHelp?: boolean;
    hideNotifications?: boolean;
    hideResetNotification?: boolean;
    appTitleBar?: string;
    helpURL?: string;
    logoImgURL?: string;
    showNotification?: boolean;
}

function NavBar(props: IProps) {
    const subKey = "serviceInfo";

    const {
        logout,
        state: { keycloak },
    } = useAuthenticationContext();
    const serviceInfo = useAuthenticationContext().state[subKey];

    const [showHelp, setShowHelp] = useState(props.showHelp || true);
    const [showWindowMax, setShowWindowMax] = useState(props.showWindowMax || true);
    const [username, setUsername] = useState<string>(getGlobalStates().userData && getGlobalStates().userData.userName);
    const [hideHelp, setHideHelp] = useState(!props.hideHelp);

    const [noNotificationsAreDisabled, setNoNotificationsAreDisabled] = useState(false);

    // Check cookies on mount and whenever needed
    const checkNotifications = () => {
        const allCookies = Cookies.get();
        const hasMuted = Object.keys(allCookies).some((cookieKey) => cookieKey.startsWith(COOKIE_KEY_PREFIX));
        setNoNotificationsAreDisabled(!hasMuted);
    };

    useEffect(() => {
        checkNotifications();
    }, []);

    const toggleNotifications = () => {
        const allCookies = Cookies.get();
        for (const cookieKey of Object.keys(allCookies)) {
            if (cookieKey.startsWith(COOKIE_KEY_PREFIX)) {
                Cookies.remove(cookieKey, { path: "/" });
            }
        }
        // After removing → recheck state
        checkNotifications();
    };

    const getApp = () => {
        if (props.appTitleBar) {
            return props.appTitleBar;
        }
        if (getGlobalStates().overrideAppID) {
            return getGlobalStates().overrideAppID;
        }
        if (getGlobalStates().appID) {
            return getGlobalStates().appID.value;
        }
        return null;
    };

    const [currentApp, setCurrentApp] = useState(getApp());

    useEffect(() => {
        if (serviceInfo) {
            setCurrentApp(getApp());
        }
    }, [serviceInfo]);

    useEffect(() => {
        if (props.appTitleBar) {
            setCurrentApp(props.appTitleBar);
        }
    }, [props.appTitleBar]);

    useEffect(() => {
        if (keycloak && keycloak.authenticated) {
            setUsername(getGlobalStates().userData?.userName || keycloak.tokenParsed?.preferred_username);
        }
    }, [keycloak]);

    const toggleFullScreen = () => {
        screenfull.toggle();
    };

    const logoutAndRefreshPage = () => {
        logout();
    };

    const { t } = useTranslation();
    const clientId = getGlobalStates().keycloak?.tokenParsed?.preferred_username;

    return (
        <header className="userful-main-header nonselect">
            <div className="userful-main-header-container">
                {props.navigation && (
                    <UserfulTooltip placement="bottom" title="Toggle Navigation">
                        <Button
                            variant="secondary"
                            iconOnly
                            onClick={() => {
                                props.navigation();
                            }}
                        >
                            <FontAwesomeIcon icon={faBars} />
                        </Button>
                    </UserfulTooltip>
                )}
                <a href={getRootPath() + getLocationSearch()}>
                    <img
                        draggable="false"
                        className="userful-main-header-logo"
                        src={props.logoImgURL || "./images/userful-secondary-black.svg"}
                        alt="Userful"
                    ></img>
                </a>
                <NavBarTitle currentApp={currentApp} />
            </div>
            {props.showNotification && <PerpetualModeBanner />}
            <div className="userful-main-header-container">
                {hideHelp && showHelp && (
                    <UserfulTooltip placement="bottom" title={t("Navbar.tooltip.help")}>
                        <Button
                            variant={"secondary"}
                            iconOnly
                            onClick={() => {
                                window.open(getGlobalStates().helpURLOverride || props.helpURL, "_blank").focus();
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleQuestion} />
                        </Button>
                    </UserfulTooltip>
                )}
                {showWindowMax && (
                    <UserfulTooltip
                        placement="bottom"
                        title={
                            t("Navbar.tooltip.fullscreen") === "Navbar.tooltip.fullscreen"
                                ? "Fullscreen"
                                : t("Navbar.tooltip.fullscreen")
                        }
                    >
                        <Button variant={"secondary"} iconOnly onClick={() => toggleFullScreen()}>
                            <FontAwesomeIcon icon={faExpand} />
                        </Button>
                    </UserfulTooltip>
                )}
                {/* {!!username && props.showNotification && <CorsWarning />} */}
                {!!username && props.showNotification && props.appTitleBar !== "GCM" && <CapacityNotification />}
                {!!username && (
                    <Dropdown drop="down" align="end">
                        <Dropdown.Toggle bsPrefix="userful-main-header-account" id="dropdownToggle" as={Button}>
                            <p>{clientId && clientId[0].toUpperCase()}</p>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="userful-main-header-account-menu">
                            <div className="userful-main-header-account-header">
                                <div className="userful-main-header-account-icon">
                                    <p>{clientId && clientId[0].toUpperCase()}</p>
                                </div>
                                <div className="userful-main-header-account-text">{username}</div>
                            </div>
                            <hr />
                            {!props.hideNotifications && !props.hideResetNotification && (
                                <Dropdown.Item
                                    disabled={noNotificationsAreDisabled}
                                    onClick={() => toggleNotifications()}
                                >
                                    <DropdownItem text={t("CommonUI.Header.resetNotification")} icon={faBell} />
                                </Dropdown.Item>
                            )}
                            {logout && (
                                <Dropdown.Item
                                    onClick={() => {
                                        if (props.onLogout) props.onLogout();

                                        logoutAndRefreshPage();
                                    }}
                                >
                                    <DropdownItem
                                        text={t("CommonUI.Header.userLogout")}
                                        icon={faArrowRightFromBracket}
                                    />
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>
        </header>
    );
}

export default NavBar;
